import { Injectable } from '@angular/core';
import { Observable, defer, first, map, of, timeout } from 'rxjs';
import { LoggerService } from '@core/services/logger/logger.service';
import { LabBrokerQuery } from '../state/lab-broker.query';
import { LabBrokerStore } from '../state/lab-broker.store';
import { LabBrokerViewState } from '../state/lab-broker.model';
import { CloseReason } from '../models/lab-broker-event.model';
import { LAB_BROKER_TIMEOUTS } from '../consts';

/**
 * Service responsible for initiating and managing the close handshake with the Lab Broker iframe.
 *
 * The close handshake flow:
 * 1. Set `pendingCloseReason` in the store (e.g., 'NAVIGATE_AWAY' or 'FALLBACK')
 * 2. The `LabBrokerViewContainerFacade.handleCloseRequests()` subscription picks this up
 * 3. It sends `RxExtensionClose(reason)` to the iframe
 * 4. Wait for the iframe to respond with `RxStatus(READY_TO_CLOSE)` or timeout
 * 5. On response/timeout, `pendingCloseReason` is cleared and the Observable completes
 */
@Injectable({ providedIn: 'root' })
export class LabBrokerCloseService {
	private readonly serviceName = 'LabBrokerCloseService';

	constructor(
		private readonly store: LabBrokerStore,
		private readonly query: LabBrokerQuery,
		private readonly logger: LoggerService
	) {}

	/**
	 * Initiates the close handshake with the Lab Broker iframe.
	 *
	 * If no handshake is required (iframe not in a state that needs it), completes immediately.
	 * Otherwise, sets `pendingCloseReason` and waits for the iframe to acknowledge or timeout.
	 *
	 * @param reason - The close reason to send ('NAVIGATE_AWAY' or 'FALLBACK')
	 * @returns Observable that completes when the close is acknowledged or times out
	 */
	initiateClose(reason: CloseReason): Observable<void> {
		return defer(() => {
			if (!this.query.requiresCloseHandshake) {
				// No handshake needed — the iframe is still live. When the scanner
				// triggered the save it will navigate away immediately after, so
				// remove the iframe now to prevent the partner's beforeunload from firing.
				if (this.query.isActive) {
					this.store.update({ viewState: LabBrokerViewState.Inactive });
				}

				return of(undefined);
			}

			this.store.update({ pendingCloseReason: reason, viewState: LabBrokerViewState.Closing });

			return this.query.isClosePending$.pipe(
				first(isClosePending => !isClosePending),
				timeout({ first: LAB_BROKER_TIMEOUTS.ClosingTimerMs, with: () => this.handleCloseTimeout(reason) }),
				map(() => undefined)
			);
		});
	}

	private handleCloseTimeout(reason: CloseReason): Observable<void> {
		if (this.query.viewState === LabBrokerViewState.Active) {
			this.logger.warn('RXLog - Custom Rx close request was not picked up by the view, continuing.', {
				module: this.serviceName,
				extendedParameters: { reason }
			});
		} else {
			this.logger.info('RXLog - Custom Rx did not acknowledge the close in time, continuing.', {
				module: this.serviceName,
				extendedParameters: { reason }
			});
		}

		this.store.update({ pendingCloseReason: null });

		return of(undefined);
	}
}
